'use client';

import { useTranslations } from 'next-intl';
import { useId } from 'react';
import type { OfflineAvatarResolution, OfflineAvatarWatermarkPosition } from '@/app/types';
import { cn } from '@/app/utils';
import { OFFLINE_AVATAR_RESOLUTIONS } from '../constants';

export interface StudioOutputSettings {
  resolution: OfflineAvatarResolution;
  watermark: boolean;
  watermarkUrl: string;
  watermarkPosition: OfflineAvatarWatermarkPosition;
}

export function OfflineDigitalHumanSettings({
  settings,
  onChange,
}: {
  settings: StudioOutputSettings;
  onChange: (patch: Partial<StudioOutputSettings>) => void;
}) {
  const t = useTranslations('tools.offlineDigitalHuman.studio');
  const groupId = useId();

  return (
    <section aria-label={t('settings.title')}>
      <div className="flex flex-col items-start gap-3">
        <p id={`${groupId}-label`} className="font-medium text-sm text-system-white80">
          {t('settings.resolution')}
        </p>
        <div
          role="radiogroup"
          aria-labelledby={`${groupId}-label`}
          className="inline-flex rounded-12 border border-system-white12 bg-black/20 p-1 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]"
        >
          {OFFLINE_AVATAR_RESOLUTIONS.map((resolution) => (
            <label
              key={resolution}
              className={cn(
                'flex h-10 min-w-[112px] cursor-pointer items-center justify-center rounded-8 px-5 font-medium text-sm transition-[background-color,color,box-shadow] focus-within:outline focus-within:outline-2 focus-within:outline-system-blue01 focus-within:outline-offset-2 motion-reduce:transition-none sm:min-w-[96px] sm:px-4',
                settings.resolution === resolution
                  ? 'bg-system-white text-system-black shadow-[0_2px_10px_rgba(0,0,0,0.28)]'
                  : 'text-system-white48 hover:bg-system-white06 hover:text-system-white80',
              )}
            >
              <input
                type="radio"
                name={`${groupId}-resolution`}
                value={resolution}
                checked={settings.resolution === resolution}
                onChange={() => onChange({ resolution })}
                className="sr-only"
              />
              {resolution.toUpperCase()}
            </label>
          ))}
        </div>
      </div>
    </section>
  );
}
